/** QuickRouter — Paires différentielles : routage couplé et contrôle des longueurs.
 *
 * Une paire = deux nets dont l'un (au moins) désigne l'autre par `pair`. Largeur w et écart s
 * viennent de la classe ou de l'impédance du net (shared/netclasses.js, calculés sur
 * l'empilage). Le routage pose l'axe de la paire (fourni, ou trouvé par un A* sur grille qui
 * évite le cuivre des autres nets avec la demi-largeur de la paire), puis deux pistes décalées
 * de ±(w + s)/2, raccordées au centre des pastilles. Pas de serpentin d'accord en v0.1 : la
 * différence de longueur est mesurée et signalée.
 */
import { padShape, segShape, discShape, shapeDist, shapeBox, pointInPoly, pointSegDist, polyBox, dist } from './geom.js';
import { allPads, padNetMap } from './netlist.js';
import { netRule } from './netclasses.js';
import { copperLayers, layerRole } from './layers.js';
import { addTrack } from './ops.js';

const r4 = (v) => Math.round(v * 1e4) / 1e4;

/** Longueur des pistes d'un net (mm) et nombre de vias. */
export function netLength(project, name) {
  let length = 0;
  const layers = new Set();
  for (const t of project.tracks) {
    if (t.net !== name) continue;
    layers.add(t.layer);
    for (let i = 1; i < t.pts.length; i++) length += dist(t.pts[i - 1], t.pts[i]);
  }
  return { net: name, length: r4(length), vias: project.vias.filter((v) => v.net === name).length, layers: [...layers] };
}

/** Paires distinctes du projet : [{ p, n }] (p = celui qui déclare, ordre alphabétique sinon). */
export function pairsOf(project) {
  const seen = new Set();
  const out = [];
  for (const net of project.nets) {
    if (!net.pair) continue;
    const key = [net.name, net.pair].sort().join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ p: net.name, n: net.pair });
  }
  return out;
}

/** Rapport des longueurs (nets demandés, ou tous ceux qui ont des pistes) et des paires. */
export function lengthReport(project, nets = null) {
  const names = nets ?? [...new Set(project.tracks.map((t) => t.net).filter(Boolean))];
  return {
    nets: names.map((n) => netLength(project, n)),
    pairs: pairsOf(project).map(({ p, n }) => {
      const a = netLength(project, p), b = netLength(project, n);
      return { p, n, lengthP: a.length, lengthN: b.length, skew: r4(Math.abs(a.length - b.length)) };
    }),
  };
}

const segsOf = (project, name) => project.tracks.filter((t) => t.net === name)
  .flatMap((t) => t.pts.slice(1).map((q, i) => ({ a: t.pts[i], b: q, w: t.width, layer: t.layer })));

const angle = (s) => Math.atan2(s.b[1] - s.a[1], s.b[0] - s.a[0]);
const parallel = (s, t) => { const d = Math.abs(((angle(s) - angle(t)) % Math.PI + Math.PI) % Math.PI); return d < 0.09 || Math.PI - d < 0.09; };

/** Contrôle de chaque paire : partenaire, longueurs, écart et largeur par rapport à la règle. */
export function pairReport(project) {
  return pairsOf(project).map(({ p, n }) => {
    const netP = project.nets.find((x) => x.name === p);
    const netN = project.nets.find((x) => x.name === n);
    const out = { p, n, partnerFound: !!netN, lengthP: 0, lengthN: 0, maxSkew: project.rules.pairSkew, gapIssues: [], widthIssues: [], impedance: null };
    if (!netN) return out;
    const sp = segsOf(project, p), sn = segsOf(project, n);
    out.lengthP = r4(sp.reduce((s, x) => s + dist(x.a, x.b), 0));
    out.lengthN = r4(sn.reduce((s, x) => s + dist(x.a, x.b), 0));
    const layers = new Set([...sp, ...sn].map((x) => x.layer));
    for (const L of layers) {
      const rule = netRule(project, netP, L) ?? netRule(project, netN, L);
      if (!rule?.gap) continue;
      out.impedance = rule.impedance;
      out.maxSkew = rule.skew ?? project.rules.pairSkew;
      // Largeur : ±10 % autour de la largeur calculée.
      for (const x of [...sp, ...sn].filter((y) => y.layer === L)) {
        if (Math.abs(x.w - rule.width) > rule.width * 0.1 + 1e-6 && out.widthIssues.length < 3) out.widthIssues.push({ layer: L, width: x.w, expected: rule.width, at: x.a });
      }
      // Écart bord à bord entre segments parallèles et voisins des deux nets.
      for (const a of sp.filter((x) => x.layer === L)) {
        for (const b of sn.filter((x) => x.layer === L)) {
          if (!parallel(a, b) || dist(a.a, a.b) < 0.5) continue;
          const d = shapeDist(segShape(a.a, a.b, a.w), segShape(b.a, b.b, b.w));
          if (d > rule.gap * 4) continue;
          if (Math.abs(d - rule.gap) > project.rules.pairTolerance + 1e-6 && out.gapIssues.length < 3) out.gapIssues.push({ layer: L, gap: r4(d), expected: rule.gap, at: a.a });
        }
      }
    }
    return out;
  });
}

// ── Routage d'une paire ────────────────────────────────────────

/** Décalage d'une polyligne de d (positif = à gauche du sens de parcours), raccords en onglet. */
export function offsetPolyline(pts, d) {
  const n = pts.length;
  const normal = (a, b) => { const l = dist(a, b) || 1; return [-(b[1] - a[1]) / l, (b[0] - a[0]) / l]; };
  const out = [];
  for (let i = 0; i < n; i++) {
    if (i === 0 || i === n - 1) {
      const nm = i === 0 ? normal(pts[0], pts[1]) : normal(pts[n - 2], pts[n - 1]);
      out.push([r4(pts[i][0] + nm[0] * d), r4(pts[i][1] + nm[1] * d)]);
      continue;
    }
    const n1 = normal(pts[i - 1], pts[i]), n2 = normal(pts[i], pts[i + 1]);
    const bx = n1[0] + n2[0], by = n1[1] + n2[1];
    const bl = Math.hypot(bx, by) || 1;
    const cos = (n1[0] * bx + n1[1] * by) / bl; // cos du demi-angle
    const k = d / Math.max(0.3, cos);
    out.push([r4(pts[i][0] + (bx / bl) * k), r4(pts[i][1] + (by / bl) * k)]);
  }
  return out;
}

/** Axe de la paire par A* 8 directions sur grille, en évitant le cuivre des autres nets. */
function pairAxis(project, lookup, { from, to, layer, halfEnvelope, nets }) {
  const g = project.rules.grid;
  const [x0, y0, x1, y1] = polyBox(project.board.outline);
  const nx = Math.floor((x1 - x0) / g) + 1, ny = Math.floor((y1 - y0) / g) + 1;
  const N = nx * ny;
  const blocked = new Uint8Array(N);
  const m = project.rules.clearance + halfEnvelope;
  const block = (shape, margin) => {
    const b = shapeBox(shape);
    const i0 = Math.max(0, Math.floor((b[0] - margin - x0) / g)), i1 = Math.min(nx - 1, Math.ceil((b[2] + margin - x0) / g));
    const j0 = Math.max(0, Math.floor((b[1] - margin - y0) / g)), j1 = Math.min(ny - 1, Math.ceil((b[3] + margin - y0) / g));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const p = [x0 + i * g, y0 + j * g];
      if (shapeDist({ kind: 'cap', a: p, b: p, r: 0 }, shape) < margin) blocked[j * nx + i] = 1;
    }
  };
  const netOf = padNetMap(project);
  for (const p of allPads(project, lookup)) {
    if (nets.has(netOf.get(p.key))) continue;
    if (p.layers.includes(layer) || (p.drill && !p.plated)) block(p.layers.length ? padShape(p) : discShape(p.pos, p.drill), m);
  }
  for (const t of project.tracks) if (t.layer === layer && !nets.has(t.net)) for (let i = 1; i < t.pts.length; i++) block(segShape(t.pts[i - 1], t.pts[i], t.width), m);
  for (const v of project.vias) if (!nets.has(v.net)) block(discShape(v.pos, v.diameter), m);
  const outline = project.board.outline;
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const p = [x0 + i * g, y0 + j * g];
    if (!pointInPoly(p, outline)) { blocked[j * nx + i] = 1; continue; }
    for (let a = 0, b = outline.length - 1; a < outline.length; b = a++) if (pointSegDist(p, outline[b], outline[a]) < project.rules.edgeClearance + halfEnvelope) { blocked[j * nx + i] = 1; break; }
  }
  const cell = (p) => [Math.round((p[0] - x0) / g), Math.round((p[1] - y0) / g)];
  const [si, sj] = cell(from), [ti, tj] = cell(to);
  const start = sj * nx + si, goal = tj * nx + ti;
  // Les abords des pastilles de départ et d'arrivée sont libérés (on y entre forcément).
  for (const [ci, cj] of [[si, sj], [ti, tj]]) for (let dj = -3; dj <= 3; dj++) for (let di = -3; di <= 3; di++) {
    const i = ci + di, j = cj + dj;
    if (i >= 0 && j >= 0 && i < nx && j < ny) blocked[j * nx + i] = 0;
  }
  const D = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
  const gArr = new Float64Array(N).fill(Infinity), par = new Int32Array(N).fill(-1), dirOf = new Int8Array(N).fill(-1);
  const open = [[0, start]];
  gArr[start] = 0;
  const h = (c) => { const i = c % nx, j = (c - i) / nx; const dx = Math.abs(i - ti), dy = Math.abs(j - tj); return Math.max(dx, dy) + 0.414 * Math.min(dx, dy); };
  let found = false;
  let guard = 0;
  while (open.length && guard++ < 2_000_000) {
    // Tas minimal simple (tableaux de taille modeste pour une paire).
    let bi = 0;
    for (let k = 1; k < open.length; k++) if (open[k][0] < open[bi][0]) bi = k;
    const [, c] = open[bi];
    open[bi] = open[open.length - 1]; open.pop();
    if (c === goal) { found = true; break; }
    const i = c % nx, j = (c - i) / nx;
    for (let d = 0; d < 8; d++) {
      const ni = i + D[d][0], nj = j + D[d][1];
      if (ni < 0 || nj < 0 || ni >= nx || nj >= ny) continue;
      const nc = nj * nx + ni;
      if (blocked[nc]) continue;
      // Une paire tourne mal : forte pénalité aux virages serrés (> 45°).
      let cost = d & 1 ? Math.SQRT2 : 1;
      if (dirOf[c] >= 0) { const t = Math.abs(d - dirOf[c]); const tt = Math.min(t, 8 - t); cost += [0, 0.3, 4, 20, 30][tt]; }
      if (gArr[c] + cost < gArr[nc]) { gArr[nc] = gArr[c] + cost; par[nc] = c; dirOf[nc] = d; open.push([gArr[nc] + h(nc), nc]); }
    }
  }
  if (!found) return null;
  const cells = [];
  for (let c = goal; c >= 0; c = par[c]) cells.push(c);
  cells.reverse();
  const pts = cells.map((c) => { const i = c % nx; return [r4(x0 + i * g), r4(y0 + ((c - i) / nx) * g)]; });
  // Fusion des points alignés.
  const out = [pts[0]];
  for (let k = 1; k < pts.length - 1; k++) {
    const a = out[out.length - 1], b = pts[k], c = pts[k + 1];
    if (Math.abs((b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0])) > 1e-6) out.push(b);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * Route une paire. opts : { p, n, layer = 'F', path (axe fourni) | auto }.
 * Rend { tracks, w, s, layer, lengthP, lengthN, skew, warnings }.
 */
export function routeDiffPair(project, lookup, { p, n, layer = 'F', path = null }) {
  const netP = project.nets.find((x) => x.name === p), netN = project.nets.find((x) => x.name === n);
  if (!netP || !netN) throw new Error(`paire ${p} / ${n} : net introuvable`);
  if (!copperLayers(project.board).includes(layer)) throw new Error(`couche ${layer} absente de la carte`);
  if (layerRole(project.board, layer) === 'plane') throw new Error(`${layer} est une couche de plan : routez la paire sur une couche de signal`);
  const rule = netRule(project, { ...netP, pair: netP.pair ?? n }, layer);
  if (!rule?.gap) throw new Error(`paire ${p} / ${n} : aucune impédance ni classe différentielle (qr_set_netclass)`);
  const w = rule.width, s = rule.gap, off = (w + s) / 2;
  const warnings = [];
  const pads = allPads(project, lookup);
  const pp = pads.filter((x) => netP.pins.includes(x.key));
  const pn = pads.filter((x) => netN.pins.includes(x.key));
  if (pp.length !== 2 || pn.length !== 2) warnings.push(`la paire relie ${pp.length} et ${pn.length} pastilles : seules la première et la plus éloignée de chaque net sont reliées (point à point)`);
  if (pp.length < 2 || pn.length < 2) throw new Error(`paire ${p} / ${n} : il faut deux pastilles par net`);
  // Extrémités : une pastille P, la N la plus proche ; à l'autre bout, la P la plus éloignée.
  const a = pp[0];
  const aN = pn.reduce((m, x) => (dist(x.pos, a.pos) < dist(m.pos, a.pos) ? x : m));
  const b = pp.reduce((m, x) => (dist(x.pos, a.pos) > dist(m.pos, a.pos) ? x : m));
  const bN = pn.filter((x) => x !== aN).reduce((m, x) => (dist(x.pos, b.pos) < dist(m.pos, b.pos) ? x : m));
  const mid = (u, v) => [(u.pos[0] + v.pos[0]) / 2, (u.pos[1] + v.pos[1]) / 2];
  const from = mid(a, aN), to = mid(b, bN);
  const axis = path ?? pairAxis(project, lookup, { from, to, layer, halfEnvelope: off + w / 2, nets: new Set([p, n]) });
  if (!axis || axis.length < 2) throw new Error(`paire ${p} / ${n} : aucun passage trouvé sur ${layer} (dégager la zone ou fournir l'axe « path »)`);
  const left = offsetPolyline(axis, off), right = offsetPolyline(axis, -off);
  // P du côté de sa pastille de départ.
  const pLeft = dist(left[0], a.pos) <= dist(right[0], a.pos);
  const lp = pLeft ? left : right, ln = pLeft ? right : left;
  if (dist(lp[lp.length - 1], b.pos) > dist(ln[ln.length - 1], b.pos) + 1e-6) warnings.push('les polarités sont croisées entre les deux extrémités : inverser P/N à une extrémité (si le protocole le permet) ou croiser la paire par un via');
  const tP = addTrack(project, { net: p, layer, width: w, pts: [a.pos, ...lp, b.pos] });
  const tN = addTrack(project, { net: n, layer, width: w, pts: [aN.pos, ...ln, bN.pos] });
  const lenP = netLength(project, p).length, lenN = netLength(project, n).length;
  const skew = r4(Math.abs(lenP - lenN));
  if (skew > (rule.skew ?? project.rules.pairSkew)) warnings.push(`différence de longueur ${skew} mm > ${rule.skew ?? project.rules.pairSkew} mm : à compenser (serpentin) sur la piste la plus courte`);
  return { tracks: [tP.id, tN.id], w, s, layer, impedance: rule.impedance, achieved: rule.achieved, lengthP: lenP, lengthN: lenN, skew, warnings };
}
